import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignaturePad } from "@/components/SignaturePad";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, CheckCircle2, Loader2, AlertTriangle, Globe } from "lucide-react";

const normRego = (r) => (r || "").toUpperCase().replace(/\s+/g, "");

const LANGS = [
  { code: "en", name: "English" },
  { code: "zh", name: "简体中文" },
  { code: "hi", name: "हिन्दी" },
  { code: "tl", name: "Tagalog" },
  { code: "sm", name: "Gagana Sāmoa" },
  { code: "mi", name: "Te Reo Māori" },
];

const T = {
  en: { language: "Language", title: "Load check-in", intro: "Fill in your truck and load. The yard office takes it from there.", rego: "Truck rego", company: "Company", name: "Your name", optional: "optional", carting: "What are you carting?", condition: "Soil condition", signature: "Your signature", selectSoil: "Select soil type", selectCond: "Select condition", found: "We found your details", captured: "Signature captured", submit: "Submit load", submitting: "Submitting…", okTitle: "Checked in", okSub: "The office has your load. Your docket is", another: "Check in another load", badTitle: "Link not valid", badSub: "This check-in link is no longer active. Please ask the yard office for the current QR code.", errRego: "Please enter your truck rego", errCompany: "Please enter your company", errSoil: "Please choose what you're carting", errCond: "Please choose the soil condition", errSign: "Please sign in the box", errGeneric: "Could not submit. Please try again.", soil: { topsoil: "Topsoil", clay: "Clay", mixed: "Mixed topsoil/clay" }, cond: { dry: "Dry", damp: "Damp", wet: "Wet", unsuitable: "Unsuitable" } },
  zh: { language: "语言", title: "卸料登记", intro: "请填写您的货车和装载信息，之后由场地办公室处理。", rego: "车牌号", company: "公司", name: "您的姓名", optional: "可选", carting: "您运载的是什么？", condition: "土壤状况", signature: "您的签名", selectSoil: "选择土壤类型", selectCond: "选择状况", found: "已找到您的信息", captured: "已采集签名", submit: "提交装载", submitting: "提交中…", okTitle: "登记完成", okSub: "办公室已收到您的装载信息。您的单号是", another: "登记另一车装载", badTitle: "链接无效", badSub: "此登记链接已失效。请向场地办公室索取最新二维码。", errRego: "请输入车牌号", errCompany: "请输入公司名称", errSoil: "请选择您运载的类型", errCond: "请选择土壤状况", errSign: "请在方框内签名", errGeneric: "提交失败，请重试。", soil: { topsoil: "表土", clay: "黏土", mixed: "表土/黏土混合" }, cond: { dry: "干", damp: "潮湿", wet: "湿", unsuitable: "不合格" } },
  hi: { language: "भाषा", title: "लोड चेक-इन", intro: "अपने ट्रक और लोड की जानकारी भरें। बाकी काम यार्ड कार्यालय करेगा।", rego: "ट्रक रजिस्ट्रेशन", company: "कंपनी", name: "आपका नाम", optional: "वैकल्पिक", carting: "आप क्या ढो रहे हैं?", condition: "मिट्टी की स्थिति", signature: "आपका हस्ताक्षर", selectSoil: "मिट्टी का प्रकार चुनें", selectCond: "स्थिति चुनें", found: "आपका विवरण मिल गया", captured: "हस्ताक्षर दर्ज किया गया", submit: "लोड सबमिट करें", submitting: "सबमिट हो रहा है…", okTitle: "चेक-इन हो गया", okSub: "कार्यालय को आपका लोड मिल गया है। आपका डॉकेट है", another: "दूसरा लोड चेक-इन करें", badTitle: "लिंक मान्य नहीं", badSub: "यह चेक-इन लिंक अब सक्रिय नहीं है। कृपया यार्ड कार्यालय से नया QR कोड लें।", errRego: "कृपया अपना ट्रक रजिस्ट्रेशन दर्ज करें", errCompany: "कृपया अपनी कंपनी दर्ज करें", errSoil: "कृपया चुनें कि आप क्या ढो रहे हैं", errCond: "कृपया मिट्टी की स्थिति चुनें", errSign: "कृपया बॉक्स में हस्ताक्षर करें", errGeneric: "सबमिट नहीं हो सका। कृपया पुनः प्रयास करें।", soil: { topsoil: "ऊपरी मिट्टी", clay: "चिकनी मिट्टी", mixed: "ऊपरी/चिकनी मिट्टी मिश्रित" }, cond: { dry: "सूखी", damp: "नम", wet: "गीली", unsuitable: "अनुपयुक्त" } },
  tl: { language: "Wika", title: "Pag-check-in ng Load", intro: "Punan ang iyong trak at load. Bahala na ang opisina ng yard sa iba.", rego: "Rehistro ng trak", company: "Kumpanya", name: "Iyong pangalan", optional: "opsyonal", carting: "Ano ang iyong kargada?", condition: "Kondisyon ng lupa", signature: "Iyong pirma", selectSoil: "Pumili ng uri ng lupa", selectCond: "Pumili ng kondisyon", found: "Nahanap namin ang iyong detalye", captured: "Nakuha ang pirma", submit: "Isumite ang load", submitting: "Isinusumite…", okTitle: "Naka-check in", okSub: "Natanggap na ng opisina ang iyong load. Ang iyong docket ay", another: "Mag-check in ng isa pang load", badTitle: "Hindi wasto ang link", badSub: "Hindi na aktibo ang link na ito. Mangyaring humingi ng bagong QR code sa opisina ng yard.", errRego: "Pakilagay ang rehistro ng iyong trak", errCompany: "Pakilagay ang iyong kumpanya", errSoil: "Pakipili kung ano ang iyong kargada", errCond: "Pakipili ang kondisyon ng lupa", errSign: "Mangyaring pumirma sa kahon", errGeneric: "Hindi naisumite. Pakisubukang muli.", soil: { topsoil: "Topsoil", clay: "Luwad (clay)", mixed: "Halong topsoil/luwad" }, cond: { dry: "Tuyo", damp: "Mamasa-masa", wet: "Basa", unsuitable: "Hindi angkop" } },
  sm: { language: "Gagana", title: "Siaki o le uta", intro: "Faatumu au faamatalaga o le loli ma le uta. O le a fafoeina e le ofisa le isi vaega.", rego: "Numera o le loli", company: "Kamupani", name: "Lou igoa", optional: "e le faʻamalosia", carting: "O le a le mea o loʻo e aveina?", condition: "Tulaga o le palapala", signature: "Lou saini", selectSoil: "Filifili le ituaiga palapala", selectCond: "Filifili le tulaga", found: "Ua maua au faamatalaga", captured: "Ua maua le saini", submit: "Lafo le uta", submitting: "O loʻo lafoina…", okTitle: "Ua siaki", okSub: "Ua maua e le ofisa lau uta. O lau pepa o le", another: "Siaki se isi uta", badTitle: "E le aoga le soʻotaga", badSub: "Ua le toe aoga lenei soʻotaga. Faamolemole fesili i le ofisa mo se QR fou.", errRego: "Faamolemole tusi le numera o lou loli", errCompany: "Faamolemole tusi lau kamupani", errSoil: "Faamolemole filifili le mea o loʻo e aveina", errCond: "Faamolemole filifili le tulaga o le palapala", errSign: "Faamolemole saini i totonu o le pusa", errGeneric: "E leʻi mafai ona lafo. Faamolemole toe taumafai.", soil: { topsoil: "Palapala pito i luga", clay: "ʻEleʻele omea", mixed: "Palapala/omea fefiloi" }, cond: { dry: "Mago", damp: "Susu", wet: "Susu tele", unsuitable: "Le talafeagai" } },
  mi: { language: "Reo", title: "Takitaki utanga", intro: "Whakakīa ō taipitopito taraka me te utanga. Mā te tari o te papa ngā toenga.", rego: "Rēhita taraka", company: "Kamupene", name: "Tō ingoa", optional: "kōwhiringa", carting: "He aha tāu e kawe nei?", condition: "Āhua o te oneone", signature: "Tō waitohu", selectSoil: "Tīpakohia te momo oneone", selectCond: "Tīpakohia te āhua", found: "Kua kitea ō taipitopito", captured: "Kua mau te waitohu", submit: "Tukua te utanga", submitting: "E tuku ana…", okTitle: "Kua takitaki", okSub: "Kua riro i te tari tō utanga. Ko tō tīkiti", another: "Takitaki anō tētahi utanga", badTitle: "Kāore te hononga e whaimana", badSub: "Kua kore tēnei hononga e mahi. Tēnā, pātai atu ki te tari mō te waehere QR hou.", errRego: "Tēnā whakaurua tō rēhita taraka", errCompany: "Tēnā whakaurua tō kamupene", errSoil: "Tēnā tīpakohia tāu e kawe nei", errCond: "Tēnā tīpakohia te āhua o te oneone", errSign: "Tēnā waitohua ki te pouaka", errGeneric: "Kāore i taea te tuku. Tēnā, ngana anō.", soil: { topsoil: "Oneone matamua", clay: "Uku", mixed: "Oneone/uku whakaranu" }, cond: { dry: "Maroke", damp: "Mākūkū", wet: "Mākū", unsuitable: "Kāore e tika" } },
};

const detectLang = () => {
  const saved = localStorage.getItem("fl_driver_lang");
  if (saved && T[saved]) return saved;
  const nav = (navigator.language || "en").toLowerCase();
  const hit = LANGS.find((l) => nav.startsWith(l.code));
  return hit ? hit.code : "en";
};

export default function DriverIntake() {
  const { token } = useParams();
  const [lang, setLang] = useState(detectLang);
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState("loading");
  const [form, setForm] = useState({ rego: "", company: "", driver_name: "", soil_type: "", condition: "" });
  const [signature, setSignature] = useState(null);
  const [autofilled, setAutofilled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const sigResetRef = useRef(0);
  const t = T[lang] || T.en;

  useEffect(() => { localStorage.setItem("fl_driver_lang", lang); }, [lang]);

  useEffect(() => {
    api.get(`/public/landfill/intake/${token}`)
      .then((r) => { setInfo(r.data); setStatus("ready"); })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const onRegoBlur = async () => {
    const rego = normRego(form.rego);
    if (!rego) return;
    try {
      const { data } = await api.get(`/public/landfill/intake/${token}/truck/${encodeURIComponent(rego)}`);
      if (data && (data.company || data.driver_name)) {
        setForm((f) => ({ ...f, company: f.company || data.company || "", driver_name: f.driver_name || data.driver_name || "" }));
        setAutofilled(true);
      }
    } catch { /* ignore */ }
  };

  const LangPicker = () => (
    <div className="flex items-center gap-2">
      <Globe className="h-4 w-4 text-white/70" />
      <Select value={lang} onValueChange={setLang}>
        <SelectTrigger className="h-8 w-[150px] border-white/20 bg-white/10 text-white" data-testid="driver-lang-select"><SelectValue /></SelectTrigger>
        <SelectContent>{LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`driver-lang-${l.code}`}>{l.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  const submit = async () => {
    setError("");
    if (!normRego(form.rego)) return setError(t.errRego);
    if (!form.company.trim()) return setError(t.errCompany);
    if (!form.soil_type) return setError(t.errSoil);
    if (!form.condition) return setError(t.errCond);
    if (!signature) return setError(t.errSign);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/landfill/intake/${token}`, {
        rego: normRego(form.rego), company: form.company.trim(),
        driver_name: form.driver_name.trim() || null,
        soil_type: form.soil_type, condition: form.condition, signature,
      });
      setDone(data.docket_number);
    } catch (e) {
      setError(e?.response?.data?.detail || t.errGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForNext = () => {
    setForm({ rego: "", company: "", driver_name: "", soil_type: "", condition: "" });
    setSignature(null); setAutofilled(false); setError(""); setDone(null);
    sigResetRef.current += 1;
  };

  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-zinc-100"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  }
  if (status === "invalid") {
    return (
      <div className="flex min-h-screen flex-col bg-zinc-100" data-testid="driver-intake-invalid">
        <div className="flex items-center justify-end bg-[#0F172A] px-5 py-3"><LangPicker /></div>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="max-w-sm rounded-2xl bg-white p-8 text-center shadow">
            <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
            <h1 className="mt-4 text-lg font-semibold text-zinc-900">{t.badTitle}</h1>
            <p className="mt-2 text-sm text-zinc-500">{t.badSub}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 pb-10" data-testid="driver-intake-page">
      <div className="bg-[#0F172A] px-5 py-6 text-white">
        <div className="mx-auto flex max-w-md items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF5F15]"><Truck className="h-6 w-6" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-white/60">{t.title}</p>
              <h1 className="text-lg font-semibold">{info?.org_name}</h1>
            </div>
          </div>
          <LangPicker />
        </div>
      </div>

      <div className="mx-auto max-w-md px-5">
        {done ? (
          <div className="mt-8 rounded-2xl bg-white p-8 text-center shadow" data-testid="driver-intake-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h2 className="mt-4 text-xl font-semibold text-zinc-900">{t.okTitle}</h2>
            <p className="mt-1 text-sm text-zinc-500">{t.okSub}</p>
            <p className="mt-2 font-mono text-2xl font-bold text-[#FF5F15]">{done}</p>
            <Button className="mt-6 w-full" onClick={resetForNext} data-testid="driver-intake-another">{t.another}</Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4 rounded-2xl bg-white p-5 shadow">
            <p className="text-sm text-zinc-500">{t.intro}</p>
            <div>
              <Label htmlFor="d-rego">{t.rego}</Label>
              <Input id="d-rego" data-testid="driver-rego-input" className="uppercase" value={form.rego}
                onChange={(e) => { setForm((f) => ({ ...f, rego: e.target.value })); setAutofilled(false); }}
                onBlur={onRegoBlur} placeholder="ABC123" autoCapitalize="characters" autoComplete="off" />
              {autofilled && <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600" data-testid="driver-autofill-note"><CheckCircle2 className="h-3 w-3" />{t.found}</p>}
            </div>
            <div>
              <Label htmlFor="d-company">{t.company}</Label>
              <Input id="d-company" data-testid="driver-company-input" value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="d-driver">{t.name} <span className="text-zinc-400">({t.optional})</span></Label>
              <Input id="d-driver" data-testid="driver-name-input" value={form.driver_name}
                onChange={(e) => setForm((f) => ({ ...f, driver_name: e.target.value }))} />
            </div>
            <div>
              <Label>{t.carting}</Label>
              <Select value={form.soil_type} onValueChange={(v) => setForm((f) => ({ ...f, soil_type: v }))}>
                <SelectTrigger data-testid="driver-soil-select"><SelectValue placeholder={t.selectSoil} /></SelectTrigger>
                <SelectContent>{(info?.soil_types || []).map((s) => <SelectItem key={s.value} value={s.value} data-testid={`driver-soil-${s.value}`}>{t.soil[s.value] || s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t.condition}</Label>
              <Select value={form.condition} onValueChange={(v) => setForm((f) => ({ ...f, condition: v }))}>
                <SelectTrigger data-testid="driver-condition-select"><SelectValue placeholder={t.selectCond} /></SelectTrigger>
                <SelectContent>{(info?.conditions || []).map((s) => <SelectItem key={s.value} value={s.value} data-testid={`driver-condition-${s.value}`}>{t.cond[s.value] || s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t.signature}</Label>
              <div className="mt-1">
                <SignaturePad key={sigResetRef.current} onSave={(d) => setSignature(d)} onCancel={() => setSignature(null)} testIdPrefix="driver-sig" />
              </div>
              {signature && <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600" data-testid="driver-signed-note"><CheckCircle2 className="h-3 w-3" />{t.captured}</p>}
            </div>
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="driver-intake-error">{error}</p>}
            <Button className="w-full" size="lg" onClick={submit} disabled={submitting} data-testid="driver-submit-btn">
              {submitting ? t.submitting : t.submit}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
